import type React from 'react';

import { OpenAPIParseError } from '@gitbook/openapi-parser';
import { Alert, Code, Description, EmptyState, Heading, LoadingPanel } from '@gitbook/spine-react';

import { capitalizeFirstLetter } from '../../../utils';
import type { UseResolveOpenAPIReturn } from './useResolveOpenAPI';

/**
 * Content of an OpenAPI block.
 */
export function OpenAPIBlockContent<T>(props: {
    type: 'operation' | 'schemas' | 'webhook';
    name?: string;
    resolved: UseResolveOpenAPIReturn<T>;
    children: React.ReactNode | ((props: { data: T }) => React.ReactNode);
}) {
    const { children, name, resolved, type } = props;

    if (resolved.state === 'pending') {
        return <LoadingPanel />;
    }

    if (resolved.state === 'error') {
        return (
            <Alert
                kind="danger"
                details={
                    resolved.error instanceof OpenAPIParseError
                        ? resolved.error.message
                        : `Failed to generated OpenAPI ${type}, unexpected error`
                }
            />
        );
    }

    if (!resolved.data) {
        return (
            <EmptyState className="border">
                <Heading>{capitalizeFirstLetter(type)} not found</Heading>
                <Description>
                    <p className="mb-2">
                        The {type} {name && <Code>{name}</Code>}{' '}
                        {type === 'schemas' ? 'were' : 'was'} not found in the OpenAPI
                        specification.
                    </p>
                    <p>
                        Update the OpenAPI specification to include the {type} or remove the block.
                    </p>
                </Description>
            </EmptyState>
        );
    }

    return typeof children === 'function' ? children({ data: resolved.data }) : children;
}
